/**
 * QueueView component.
 *
 * Main queue view component. Renders tabs, item list, and expansion area.
 * Uses the unified /v1/attention endpoint as the primary data source.
 * Tool approval items use the existing ApprovalCard for detail rendering.
 * Text review items use ReviewPanel. PR review items use PRReviewPanel.
 * History tab uses GET /v1/attention/history for resolved items of all types.
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { useParams } from 'react-router-dom'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button, Card, CardContent, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, Select } from '@/shared/components/ui'
import { ToastAction } from '@/shared/components/ui/toast'
import { PageLoadingState } from '@/shared/components/PageLoadingState'
import { useToast } from '@/shared/hooks'
import { QueueTabs } from './QueueTabs'
import { QueueFilters } from './QueueFilters'
import { AttentionItemRow } from './QueueItemRow'
import { QueueEmptyState } from './QueueEmptyState'
import { DegradedBanner } from './DegradedBanner'
import { SidePanelLayout } from './SidePanelLayout'
import {
  ApprovalCard,
  ApprovalCardSkeleton,
  ApprovalCardError,
  ApprovalExecutionProvider,
  ApprovalHeaderActions,
} from './approval'
import { ReviewPanel } from './ReviewPanel'
import { DebugReviewPanel } from './DebugReviewPanel'
import { PRReviewPanel } from './PRReviewPanel'
import { RejectDialog, buildSourceContext, buildProposedResponse } from './reject'
import { useQueueStore } from '../store'
import { useOperator, useAuthChecked } from '@/features/auth'
import { useOperatorStore } from '@/shared/stores'
import { formatRelativeTimeFromDate } from '../utils/time'
import type {
  AttentionItem,
  AttentionQueueTab,
  ApprovalItem,
  ApprovalDetailResponse,
  OperatorTag,
  QueueView as QueueViewName,
  QueueTypeFilter,
} from '../types'
import { isToolApproval, isTextReview, isPRReview, getReviewSubtype, matchesTypeFilter, OPERATOR_TAGS } from '../types'
import { ASSIGNMENT_ENABLED } from '../featureFlags'

/**
 * Optional non-exclusive type filter for the unified pending list (Task 4072).
 * Default 'all' shows everything; the operator may narrow to approvals or
 * reviews. This is a convenience filter, not a list partition.
 */
const TYPE_FILTER_OPTIONS: { value: QueueTypeFilter; label: string }[] = [
  { value: 'all', label: 'All types' },
  { value: 'approvals', label: 'Approvals' },
  { value: 'reviews', label: 'Reviews' },
]

/**
 * Get the panel title based on the expanded item's attention type.
 */
function getPanelTitle(item: AttentionItem | undefined): string {
  if (!item) return 'Details'
  if (isToolApproval(item.attention_type)) return 'Approval Details'
  if (isTextReview(item.attention_type)) return 'Review'
  if (isPRReview(item.attention_type)) return 'PR Review'
  return 'Details'
}

/**
 * Determine the queue tab from the URL path.
 *
 * The `my` route resolves to the `mine` tab when operator assignment is
 * supported by the backend. With assignment disabled (see featureFlags.ts)
 * we silently fall back to `all` so a stale bookmark doesn't strand the
 * operator on an empty filtered view.
 */
function useQueueTab(): AttentionQueueTab {
  const { tab } = useParams<{ tab?: string }>()

  switch (tab) {
    case 'my':
      return ASSIGNMENT_ENABLED ? 'mine' : 'all'
    case 'all':
      return 'all'
    case 'history':
      return 'history'
    default:
      return 'all'
  }
}

/**
 * Resolve the `:view` URL segment for href continuity ONLY (Task 4072).
 *
 * The queue is a single unified list — `view` no longer partitions items. We
 * still read it so the All/Mine/History sub-tab links preserve whichever
 * `/queue/{view}/...` prefix the operator arrived on (back-compat with existing
 * deep links). Unknown values fall back to 'attention'.
 */
function useQueueView(): QueueViewName {
  const { view } = useParams<{ view?: string }>()
  return view === 'responses' ? 'responses' : 'attention'
}

/**
 * Main queue view component.
 */
export function QueueView() {
  const tab = useQueueTab()
  const view = useQueueView()
  // Optional, non-exclusive type filter for the unified list. Default 'all'
  // shows everything together (the single unified list); the operator may
  // narrow to approvals/reviews as a convenience. Local view-state only.
  const [typeFilter, setTypeFilter] = React.useState<QueueTypeFilter>('all')
  const { toast } = useToast()
  const operator = useOperator()
  const operatorId = operator?.id

  // Whether the right-side detail panel is shown in its maximized
  // (modal) variant. Lives at the QueueView level rather than in the
  // store because it's purely view-state for the local browser tab.
  const [panelMaximized, setPanelMaximized] = React.useState(false)

  const {
    attentionItems,
    historyItems,
    historyHasMore,
    historyNextCursor,
    isFetching,
    error,
    degraded,
    degradedSources,
    expandedItemId,
    detailData,
    detailLoading,
    detailError,
    newItemIds,
    fadingOutIds,
    keyboardSelectedId,
    assignments,
    operatorTag,
    setTab,
    setExpandedItem,
    fetchAttentionItems,
    fetchAssignments,
    fetchHistory,
    loadMoreHistory,
    fetchDetail,
    approve,
    suppress,
    reject,
    assign,
    unassign,
    resolveItem,
    startPolling,
    startForegroundPolling,
    stopForegroundPolling,
    openAgentSessionModal,
  } = useQueueStore()

  // Reject dialog state
  const [rejectDialogItem, setRejectDialogItem] = React.useState<ApprovalItem | null>(null)
  const [rejectDialogCard, setRejectDialogCard] = React.useState<ApprovalDetailResponse['card'] | null>(null)
  // Snapshot of the original AttentionItem behind the synthetic ApprovalItem.
  // Carries the live `session_id` + `agent_id` that the synthetic shim
  // discards — used so the post-reject toast can offer a "Watch live" CTA
  // that opens the AgentSessionModal in live mode for that session.
  const [rejectDialogAttentionItem, setRejectDialogAttentionItem] =
    React.useState<AttentionItem | null>(null)
  const [rejectError, setRejectError] = React.useState<string | null>(null)

  // Escalate dialog state
  const [escalateDialogItem, setEscalateDialogItem] = React.useState<ApprovalItem | null>(null)

  // Mutation states
  const [actionPending, setActionPending] = React.useState(false)
  const [pendingAction, setPendingAction] = React.useState<'approve' | 'suppress' | 'reject' | null>(null)

  // Update tab when URL changes
  React.useEffect(() => {
    setTab(tab)
  }, [tab, setTab])

  const authChecked = useAuthChecked()

  // Background polling for the queue is started once at the AppShell
  // level so the sidebar badge stays fresh on every page. Here we only
  // force an immediate refresh whenever the operator navigates *into*
  // the queue, so the list they see is up-to-date the moment it renders.
  // (startPolling is a no-op if polling is already running, which it
  //  almost always is at this point — the call is kept defensively in
  //  case the QueueView mounts before AppShell's effect has run.)
  React.useEffect(() => {
    if (!authChecked) return
    startPolling(operatorId)
    void fetchAttentionItems()
    void fetchAssignments()
  }, [authChecked, operatorId, startPolling, fetchAttentionItems, fetchAssignments])

  // Fast (5s) foreground auto-refresh, scoped to this mounted view.
  //
  // Rationale: when an operator rejects an agent tool-approval, the agent
  // frequently chains a second approval seconds later. The app-shell
  // 10-minute loop (startPolling) is far too slow to surface that, so we
  // run a separate fast loop owned entirely by this view — started on
  // mount, torn down on unmount.
  //
  // It uses the store's lightweight hash/count check (fetchAttentionCount),
  // which only pulls the full list when the server hash changes, so a tick
  // with no change is a single cheap request and does NOT mutate state —
  // meaning it can't re-trigger this effect (the deps are the stable tab,
  // auth flag, and action refs).
  //
  // Gated off the History tab: the 5s loop exists to keep the live PENDING
  // queue fresh. The History tab renders resolved items from a different
  // endpoint (fetchHistory) and never benefits from a pending-queue poll;
  // running it there would issue pointless requests and, on a hash change,
  // trigger a fetchAttentionItems that the History view doesn't render.
  // The store's own interval guard (startForegroundPolling is a no-op while
  // already running) plus this cleanup keep StrictMode's double-invocation
  // and tab switches from stacking duplicate intervals.
  React.useEffect(() => {
    if (!authChecked) return
    if (tab === 'history') return
    startForegroundPolling()
    return () => {
      stopForegroundPolling()
    }
  }, [authChecked, tab, startForegroundPolling, stopForegroundPolling])

  // Fetch history when switching to history tab
  React.useEffect(() => {
    if (tab === 'history' && authChecked) {
      fetchHistory(operatorId)
    }
  }, [tab, authChecked, operatorId, fetchHistory])

  // Track whether the first successful fetch has completed.
  //
  // We detect this by watching `isFetching` transition from true → false
  // without an error. Driving it off initial state values would mark
  // "loaded" on first render — before any fetch has actually started —
  // which suppresses the initial loading card and leaves a blank screen
  // while the queue is still hydrating.
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false)
  const [lastUpdated, setLastUpdated] = React.useState<Date | null>(null)
  const prevIsFetching = React.useRef(false)
  React.useEffect(() => {
    if (prevIsFetching.current && !isFetching && !error) {
      setHasLoadedOnce(true)
      setLastUpdated(new Date())
    }
    prevIsFetching.current = isFetching
  }, [isFetching, error])

  // When the operator moves to a different attention item — or fully
  // closes the panel — reset the maximize state so the next item opens
  // in the default sidebar view rather than inheriting the previous
  // item's preference.
  React.useEffect(() => {
    if (!expandedItemId) {
      setPanelMaximized(false)
    }
  }, [expandedItemId])

  // Unified pending list (Task 4072): every attention type is shown together —
  // tool approvals, workflow human reviews, agent text reviews, and PR reviews.
  // The optional `typeFilter` is a non-exclusive convenience narrowing
  // (default 'all' shows everything); it does NOT partition the list into
  // separate views.
  const typeFilteredItems = React.useMemo(() => {
    if (typeFilter === 'all') return attentionItems
    return attentionItems.filter((item) => matchesTypeFilter(item.attention_type, typeFilter))
  }, [attentionItems, typeFilter])

  // Display items based on the All/Mine sub-tab. History is fetched
  // separately and bypasses this filter.
  const displayAttentionItems = React.useMemo(() => {
    if (tab === 'mine' && operatorTag) {
      return typeFilteredItems.filter((item) => assignments[item.id] === operatorTag)
    }
    return typeFilteredItems
  }, [typeFilteredItems, tab, operatorTag, assignments])

  // The "All" tab badge reflects the total of the unified pending list (after
  // the optional type filter, so the badge matches what the operator sees).
  const currentViewCount = typeFilteredItems.length

  const mineCount = React.useMemo(() => {
    if (!operatorTag) return 0
    return typeFilteredItems.filter((item) => assignments[item.id] === operatorTag).length
  }, [typeFilteredItems, assignments, operatorTag])

  // Find expanded item
  const expandedAttentionItem = displayAttentionItems.find((i) => i.id === expandedItemId)
  const expandedHistoryItem = historyItems.find((i) => i.id === expandedItemId)

  // Handle item click
  const handleAttentionItemClick = React.useCallback(
    (item: AttentionItem) => {
      setExpandedItem(item.id, item.attention_type)
    },
    [setExpandedItem],
  )

  const handleHistoryItemClick = React.useCallback(
    (item: AttentionItem) => {
      setExpandedItem(item.id, item.attention_type)
    },
    [setExpandedItem],
  )

  // Resolve the current operator's initials directly from the operator-store
  // (the splash-screen-selected operator from src-tauri's OPERATORS list).
  // We read this here — in addition to the App.tsx-driven sync into the queue
  // store's `operatorTag` — so the assign call has a guaranteed source of
  // truth for `assigned_by` regardless of:
  //   - returning users opening straight into the queue (no fresh splash
  //     pick this session — the queue store's `operatorTag` is briefly null
  //     during operator rehydration);
  //   - any future regression in the App.tsx sync effect;
  //   - localStorage `stream_operator_tag` being stale or wiped.
  // The operator-store's `selected.initials` is populated from
  // `auth.rs::OPERATORS` and always matches the backend allow-list.
  const selectedOperatorInitials = useOperatorStore(
    (s) => s.selected?.initials ?? null,
  )

  // Handle assignment
  //
  // `assigned_by` resolution priority:
  //   1. operator-store initials (the splash-screen-selected operator) — the
  //      authoritative source. Validated against OPERATOR_TAGS so a future
  //      Rust operator with non-matching initials can't sneak through.
  //   2. queue store's `operatorTag` — kept in sync by App.tsx; useful if (1)
  //      somehow returns null.
  //   3. empty string — platform-operations only validates `assigned_by` when
  //      it's non-empty, so an empty string means "no audit info" and the
  //      request goes through. Prior fallback was the literal "operator",
  //      which is not in the allow-list (MJT/BH/JW/TF) and caused a 400.
  const handleAssign = React.useCallback(
    async (itemId: string, tag: OperatorTag) => {
      const validatedFromOperatorStore =
        selectedOperatorInitials &&
        (OPERATOR_TAGS as readonly string[]).includes(selectedOperatorInitials)
          ? selectedOperatorInitials
          : null
      const assignedBy = validatedFromOperatorStore ?? operatorTag ?? ''
      try {
        await assign(itemId, tag, assignedBy)
        toast({
          title: 'Assigned',
          description: `Item assigned to ${tag}.`,
        })
      } catch (err) {
        toast({
          title: 'Failed to assign',
          description: err instanceof Error ? err.message : 'Please try again.',
        })
      }
    },
    [assign, operatorTag, selectedOperatorInitials, toast],
  )

  const handleUnassign = React.useCallback(
    async (itemId: string) => {
      try {
        await unassign(itemId)
        toast({
          title: 'Unassigned',
          description: 'Item returned to all operators.',
        })
      } catch (err) {
        toast({
          title: 'Failed to unassign',
          description: err instanceof Error ? err.message : 'Please try again.',
        })
      }
    },
    [unassign, toast],
  )

  // Handle approve action.
  //
  // The store's `approve` performs an optimistic update synchronously (removes
  // the item from the queue, closes the detail panel) and *then* awaits the
  // POST /v1/attention/{id}/resolve call. That POST can take several seconds,
  // and previously this handler awaited it before firing the confirmation toast
  // — causing a noticeable delay between the button click and any visual
  // acknowledgement.
  //
  // From the operator's perspective the approval is already done the instant
  // the optimistic update fires, so we fire the toast immediately and let the
  // network call resolve in the background.  Mirrors handleRejectSubmit.
  const handleApprove = React.useCallback(
    (item: ApprovalItem) => {
      // Fire the confirmation toast immediately — the optimistic update inside
      // approve() removes the item from the list synchronously on this tick.
      toast({
        title: 'Approved',
        description: `${item.display_name} has been approved.`,
      })

      void approve(item, operatorId ?? '')
        .then((result) => {
          // Task 3145f: surface a neutral follow-up toast when the backend
          // confirms the item was already resolved before our request landed.
          if (result.alreadyResolved) {
            toast({
              title: 'Already resolved',
              description: `${item.display_name} was already resolved.`,
            })
          }
        })
        .catch((err) => {
          // The store has already rolled back the optimistic update; let the
          // operator know so they can retry.
          toast({
            variant: 'danger',
            title: 'Approval failed',
            description:
              err instanceof Error
                ? err.message
                : 'Could not submit approval. The item has been restored to the queue.',
          })
        })
    },
    [operatorId, approve, toast],
  )

  // Handle suppress action — same fire-immediately pattern as handleApprove.
  const handleSuppress = React.useCallback(
    (item: ApprovalItem) => {
      toast({
        title: 'Suppressed',
        description: `${item.display_name} has been suppressed.`,
      })

      void suppress(item, operatorId ?? '')
        .then((result) => {
          if (result.alreadyResolved) {
            toast({
              title: 'Already resolved',
              description: `${item.display_name} was already resolved.`,
            })
          }
        })
        .catch((err) => {
          toast({
            variant: 'danger',
            title: 'Suppress failed',
            description:
              err instanceof Error
                ? err.message
                : 'Could not suppress item. The item has been restored to the queue.',
          })
        })
    },
    [operatorId, suppress, toast],
  )

  // Handle reject action - open dialog
  const handleReject = React.useCallback(
    (
      item: ApprovalItem,
      card: ApprovalDetailResponse['card'],
      attentionItem?: AttentionItem | null,
    ) => {
      setRejectDialogItem(item)
      setRejectDialogCard(card)
      setRejectDialogAttentionItem(attentionItem ?? null)
      setRejectError(null)
    },
    [],
  )

  // Handle reject submission.
  //
  // The store's `reject` performs an optimistic update synchronously
  // (removes the item from the queue, closes the detail panel) and
  // *then* awaits the POST /v1/attention/{id}/resolve call. That POST
  // can take 10–15s in the wild, during which the operator previously
  // saw the modal frozen on "Rejecting...".
  //
  // From the operator's perspective the rejection is already done the
  // instant the optimistic update fires, so we close the modal and
  // show a confirmation toast immediately, and let the network call
  // resolve in the background. The store rolls back the optimistic
  // mutation on POST failure, and we surface that via a toast.
  const handleRejectSubmit = React.useCallback(
    ({ quickReasons, feedback }: { quickReasons: string[]; feedback: string }) => {
      if (!rejectDialogItem) return
      const item = rejectDialogItem
      // Capture the AttentionItem snapshot for the "Watch live" CTA
      // before we clear dialog state below.
      const attentionSnapshot = rejectDialogAttentionItem
      const liveSessionId = attentionSnapshot?.session_id ?? null
      const liveAgentId = attentionSnapshot?.agent_id ?? null
      const isAgentSession =
        attentionSnapshot?.source === 'agent' && !!liveSessionId

      // Close modal + clear error state immediately so the operator
      // can move on to the next item in the attention queue.
      setRejectDialogItem(null)
      setRejectDialogCard(null)
      setRejectDialogAttentionItem(null)
      setRejectError(null)

      // Surface a "Watch live" CTA when the rejected item is tied to a
      // live agent session — clicking it opens the AgentSessionModal
      // (always live since Task 4053b) so the operator can see the agent
      // process the feedback in real time. Falls through to a regular
      // toast when the item isn't an agent session (e.g. workflow
      // approvals).
      toast({
        title: 'Rejection received',
        description: `${item.agent_id} will learn from this.`,
        action: isAgentSession && liveSessionId ? (
          <ToastAction
            altText="Watch the agent process your feedback live"
            onClick={() => {
              openAgentSessionModal(liveSessionId, {
                agentId: liveAgentId,
              })
            }}
          >
            Watch live
          </ToastAction>
        ) : undefined,
      })
      // Fire the resolve in the background. The store handles
      // optimistic update + rollback; we only need to surface failure
      // (or — Task 3145f — the "already_resolved" idempotent path,
      // which we surface as a neutral follow-up toast overriding the
      // optimistic "Rejection received" message).
      void reject(item, operatorId ?? '', {
        quick_reasons: quickReasons,
        feedback,
      })
        .then((result) => {
          if (result.alreadyResolved) {
            toast({
              title: 'Already resolved',
              description: `${item.display_name} was already resolved.`,
            })
          }
        })
        .catch((err) => {
          toast({
            variant: 'danger',
            title: 'Rejection failed',
            description:
              err instanceof Error
                ? err.message
                : 'Could not submit rejection. The item has been restored to the queue.',
          })
        })
    },
    [
      rejectDialogItem,
      rejectDialogAttentionItem,
      operatorId,
      reject,
      toast,
      openAgentSessionModal,
    ],
  )

  // Handle escalate click
  const handleEscalateClick = React.useCallback(
    (item: ApprovalItem) => {
      setEscalateDialogItem(item)
    },
    [],
  )

  // Handle escalate submission
  const handleEscalateSubmit = React.useCallback(
    async () => {
      if (!escalateDialogItem) return
      setActionPending(true)
      setPendingAction('reject')
      try {
        const result = await reject(escalateDialogItem, operatorId ?? '', {
          quick_reasons: ['should_escalate'],
          feedback: 'Escalated to human review',
        })
        setEscalateDialogItem(null)
        setExpandedItem(null)
        if (result.alreadyResolved) {
          toast({
            title: 'Already resolved',
            description: `${escalateDialogItem.display_name} was already resolved.`,
          })
        } else {
          toast({
            title: 'Escalated',
            description: `${escalateDialogItem.display_name} has been escalated.`,
          })
        }
      } catch (err) {
        toast({
          title: 'Failed to escalate',
          description: err instanceof Error ? err.message : 'Please try again.',
        })
      } finally {
        setActionPending(false)
        setPendingAction(null)
      }
    },
    [escalateDialogItem, operatorId, reject, toast, setExpandedItem],
  )

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <QueueTabs view={view} count={null} />
        <Card>
          <CardContent style={styles.errorContent}>
            <AlertCircle style={styles.errorIcon} />
            <div>
              <p style={styles.errorTitle}>Failed to load queue</p>
              <p style={styles.errorMessage}>
                {error}
              </p>
            </div>
            <Button onClick={() => fetchAttentionItems()} variant="secondary">
              <RefreshCw style={iconStyles.icon16mr} />
              Try again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Render an attention item row
  const renderAttentionRow = (item: AttentionItem) => (
    <AttentionItemRow
      key={item.id}
      item={item}
      tab={tab}
      operatorTag={operatorTag}
      assignedTo={assignments[item.id] ?? null}
      isExpanded={item.id === expandedItemId}
      isKeyboardSelected={item.id === keyboardSelectedId && item.id !== expandedItemId}
      isNew={newItemIds.has(item.id)}
      isFadingOut={fadingOutIds.has(item.id)}
      onItemClick={() => handleAttentionItemClick(item)}
      onAssign={(tag) => handleAssign(item.id, tag)}
      onUnassign={() => handleUnassign(item.id)}
      onSuppress={() => {
        handleSuppress({
          id: item.id,
          source: item.source,
          source_ref: item.execution_id ?? item.id,
          agent_id: item.agent_id ?? '',
          display_name: item.title,
        } as unknown as ApprovalItem)
      }}
    />
  )

  // Render a history item row (unified attention item with resolution metadata)
  const renderHistoryRow = (item: AttentionItem) => (
    <AttentionItemRow
      key={item.id}
      item={item}
      tab="history"
      operatorTag={operatorTag}
      assignedTo={assignments[item.id] ?? null}
      isExpanded={item.id === expandedItemId}
      isKeyboardSelected={item.id === keyboardSelectedId && item.id !== expandedItemId}
      isNew={false}
      isFadingOut={false}
      onItemClick={() => handleHistoryItemClick(item)}
      onAssign={(tag) => handleAssign(item.id, tag)}
      onUnassign={() => handleUnassign(item.id)}
      showResolution
    />
  )

  // Render the detail panel based on attention type
  const renderPanel = () => {
    if (expandedAttentionItem) {
      const item = expandedAttentionItem

      // Tool approval items → existing approval card
      if (isToolApproval(item.attention_type)) {
        return (
          <ApprovalCardPanel
            sessionId={item.session_id}
            executionId={item.execution_id}
            detailData={detailData}
            detailLoading={detailLoading}
            detailError={detailError}
            onRetryDetail={() => fetchDetail(item.id)}
            onApprove={() => {
              if (detailData) {
                // Bridge: create a compatible ApprovalItem shape
                handleApprove({
                  id: item.id,
                  source: item.source,
                  source_ref: item.execution_id ?? item.id,
                  agent_id: item.agent_id ?? '',
                  display_name: item.title,
                } as unknown as ApprovalItem)
              }
            }}
            onSuppress={() => {
              if (detailData) {
                handleSuppress({
                  id: item.id,
                  source: item.source,
                  source_ref: item.execution_id ?? item.id,
                  agent_id: item.agent_id ?? '',
                  display_name: item.title,
                } as unknown as ApprovalItem)
              }
            }}
            onReject={(card) => {
              handleReject(
                {
                  id: item.id,
                  source: item.source,
                  source_ref: item.execution_id ?? item.id,
                  agent_id: item.agent_id ?? '',
                  display_name: item.title,
                } as unknown as ApprovalItem,
                card,
                item,
              )
            }}
            onEscalate={() => {
              handleEscalateClick({
                id: item.id,
                source: item.source,
                source_ref: item.execution_id ?? item.id,
                agent_id: item.agent_id ?? '',
                display_name: item.title,
              } as unknown as ApprovalItem)
            }}
            actionPending={actionPending}
            pendingAction={pendingAction}
          />
        )
      }

      // Text review items → ReviewPanel (or DebugReviewPanel for debug_review subtype)
      if (isTextReview(item.attention_type)) {
        const reviewSubtype = getReviewSubtype(item)

        // Debug review items get specialised diagnostic rendering
        if (reviewSubtype === 'debug_review') {
          return (
            <DebugReviewPanel
              item={item}
              actionPending={actionPending}
              onResolve={async (reply) => {
                setActionPending(true)
                try {
                  await resolveItem(item, 'reply', operatorId ?? '', reply)
                  toast({ title: 'Replied', description: 'Debug review resolved.' })
                } catch (err) {
                  toast({ title: 'Failed', description: err instanceof Error ? err.message : 'Please try again.' })
                } finally {
                  setActionPending(false)
                }
              }}
              onDismiss={() => setExpandedItem(null)}
            />
          )
        }

        // Default text review
        return (
          <ReviewPanel
            item={item}
            actionPending={actionPending}
            onResolve={async (reply) => {
              setActionPending(true)
              try {
                await resolveItem(item, 'reply', operatorId ?? '', reply)
                toast({ title: 'Replied', description: 'Review resolved with reply.' })
              } catch (err) {
                toast({ title: 'Failed', description: err instanceof Error ? err.message : 'Please try again.' })
              } finally {
                setActionPending(false)
              }
            }}
            onDismiss={() => setExpandedItem(null)}
          />
        )
      }

      // PR review items → PRReviewPanel
      if (isPRReview(item.attention_type)) {
        return (
          <PRReviewPanel
            item={item}
            actionPending={actionPending}
            onApprove={async () => {
              setActionPending(true)
              try {
                await resolveItem(item, 'approve', operatorId ?? '')
                toast({ title: 'PR Approved', description: 'PR has been approved.' })
              } catch (err) {
                toast({ title: 'Failed', description: err instanceof Error ? err.message : 'Please try again.' })
              } finally {
                setActionPending(false)
              }
            }}
            onRequestChanges={async (feedback) => {
              setActionPending(true)
              try {
                await resolveItem(item, 'reject', operatorId ?? '', feedback)
                toast({ title: 'Changes Requested', description: 'Feedback has been sent.' })
              } catch (err) {
                toast({ title: 'Failed', description: err instanceof Error ? err.message : 'Please try again.' })
              } finally {
                setActionPending(false)
              }
            }}
            onDismiss={() => setExpandedItem(null)}
          />
        )
      }

      // Fallback
      return null
    }

    // History item selected — render a read-only view of the detail.
    // `fetchDetail` already populates detailData for tool-approval
    // history items (it searches both attentionItems and historyItems).
    // No-op action handlers are passed because the item is resolved and
    // the action bar is hidden via readOnly={true}.
    if (expandedHistoryItem) {
      const item = expandedHistoryItem

      if (isToolApproval(item.attention_type)) {
        return (
          <ApprovalCardPanel
            sessionId={item.session_id}
            executionId={item.execution_id}
            detailData={detailData}
            detailLoading={detailLoading}
            detailError={detailError}
            onRetryDetail={() => fetchDetail(item.id)}
            onApprove={() => {}}
            onSuppress={() => {}}
            onReject={() => {}}
            onEscalate={() => {}}
            actionPending={false}
            pendingAction={null}
            readOnly
          />
        )
      }

      // Text review history items — render read-only via ReviewPanel.
      // The panel's action buttons are contextually hidden by passing
      // stub handlers; ReviewPanel itself does not have a readOnly prop.
      if (isTextReview(item.attention_type)) {
        const reviewSubtype = getReviewSubtype(item)
        if (reviewSubtype === 'debug_review') {
          return (
            <DebugReviewPanel
              item={item}
              actionPending={false}
              onResolve={async () => {}}
              onDismiss={() => setExpandedItem(null)}
            />
          )
        }
        return (
          <ReviewPanel
            item={item}
            actionPending={false}
            onResolve={async () => {}}
            onDismiss={() => setExpandedItem(null)}
          />
        )
      }

      // PR review history items
      if (isPRReview(item.attention_type)) {
        return (
          <PRReviewPanel
            item={item}
            actionPending={false}
            onApprove={async () => {}}
            onRequestChanges={async () => {}}
            onDismiss={() => setExpandedItem(null)}
          />
        )
      }

      return null
    }

    return null
  }

  const isHistoryTab = tab === 'history'

  // Items to display.
  //
  // We want the operator to see cached items *immediately* when they
  // navigate into the queue, with a small "Updating…" indicator in the
  // header to signal that a fresh fetch is in flight. The full-page
  // loading card is only shown when we genuinely have nothing to render —
  // i.e. the first fetch hasn't resolved AND there are no cached items
  // from the AppShell-level background poller.
  //
  // History items are fetched on demand (not preloaded by the background
  // poller), so for the history tab we still gate on `hasLoadedOnce`.
  const showInitialLoading = isHistoryTab
    ? !hasLoadedOnce
    : !hasLoadedOnce && attentionItems.length === 0

  // History is a single unified list too (Task 4072). The history tab has its
  // own richer filter bar (QueueFilters, including a type selector), so we do
  // NOT apply the pending `typeFilter` here — history shows every resolved type
  // together by default and is narrowed via QueueFilters instead.
  const displayHistoryItems = historyItems

  const displayItems = isHistoryTab ? displayHistoryItems : displayAttentionItems
  const isEmpty = displayItems.length === 0

  return (
    <div style={styles.container}>
      {/* Single unified attention list (Task 4072): no Attention/Responses
          split. Every type is shown together; the inner All/Mine/History strip
          filters by ownership/state (orthogonal to type), and the optional type
          selector below narrows by type as a convenience without partitioning. */}

      {/* Header with sub-tabs, update indicator, and manual refresh */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <QueueTabs view={view} count={currentViewCount} mineCount={mineCount} />
          {/* Optional, non-exclusive type filter for the pending list. Hidden on
              the history tab, which has its own richer filter bar. */}
          {!isHistoryTab && (
            <Select
              value={typeFilter}
              onChange={(value) => setTypeFilter(value as QueueTypeFilter)}
              options={TYPE_FILTER_OPTIONS}
              style={styles.typeFilterSelect}
              aria-label="Filter by type"
            />
          )}
        </div>
        <div style={styles.headerRight}>
          {!showInitialLoading && (isFetching || lastUpdated) && (
            <span style={styles.updateIndicator}>
              {isFetching ? (
                <span style={styles.updatingText}>
                  <RefreshCw style={iconStyles.icon12} className="animate-spin" />
                  Updating...
                </span>
              ) : lastUpdated ? (
                `Updated ${formatRelativeTimeFromDate(lastUpdated)}`
              ) : null}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (tab === 'history') {
                fetchHistory(operatorId)
              } else {
                fetchAttentionItems()
              }
            }}
            disabled={isFetching}
            title="Refresh queue"
            aria-label="Refresh queue"
            aria-busy={isFetching}
          >
            <RefreshCw
              style={iconStyles.icon14}
              className={isFetching ? 'animate-spin' : undefined}
            />
          </Button>
        </div>
      </div>

      {/* Degraded source warning */}
      {degraded && <DegradedBanner sources={degradedSources} />}

      {/* Secondary filters (history tab only) */}
      {isHistoryTab && (
        <QueueFilters filter="history" items={historyItems} />
      )}

      {/* Content area */}
      <SidePanelLayout
        expanded={!!(expandedAttentionItem || expandedHistoryItem)}
        onClose={() => {
          // Close fully resets both the expansion and the maximize state
          // so the next item opens in the default sidebar mode rather
          // than re-using the previous operator's preference.
          setExpandedItem(null)
          setPanelMaximized(false)
        }}
        panel={renderPanel()}
        panelTitle={getPanelTitle(expandedAttentionItem ?? expandedHistoryItem)}
        maximized={panelMaximized}
        onToggleMaximize={() => setPanelMaximized((m) => !m)}
        // Consolidate the per-item chrome (View Agent Session icon +
        // more-actions dropdown) onto the side-panel's top header bar
        // so all controls share one row alongside maximize/close.
        // Only meaningful for tool-approval items today; other panel
        // types render their own chrome inline.
        panelHeaderExtras={
          (() => {
            const headerItem = expandedAttentionItem ?? expandedHistoryItem
            if (!headerItem) return null
            if (!isToolApproval(headerItem.attention_type)) return null
            return (
              <ApprovalHeaderActions
                item={headerItem}
                sessionId={headerItem.session_id}
                onEscalate={() => {
                  handleEscalateClick({
                    id: headerItem.id,
                    source: headerItem.source,
                    source_ref: headerItem.execution_id ?? headerItem.id,
                    agent_id: headerItem.agent_id ?? '',
                    display_name: headerItem.title,
                  } as unknown as ApprovalItem)
                }}
              />
            )
          })()
        }
      >
        {showInitialLoading ? (
          <PageLoadingState
            label="Loading queue…"
            hint="Fetching attention items"
          />
        ) : isEmpty && !isFetching ? (
          <QueueEmptyState tab={tab} view={view} />
        ) : (
          <div style={styles.itemList}>
            {isHistoryTab
              ? displayHistoryItems.map(renderHistoryRow)
              : displayAttentionItems.map(renderAttentionRow)
            }
            {isHistoryTab && historyHasMore && historyNextCursor && (
              <div style={{ padding: '12px 16px', textAlign: 'center' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void loadMoreHistory()}
                  disabled={isFetching}
                  style={{ color: 'var(--text-secondary)', fontSize: '13px' }}
                >
                  {isFetching ? 'Loading...' : 'Load more'}
                </Button>
              </div>
            )}
          </div>
        )}
      </SidePanelLayout>

      {/* Reject dialog */}
      <RejectDialog
        open={!!rejectDialogItem}
        onOpenChange={(open) => {
          if (!open) {
            setRejectDialogItem(null)
            setRejectDialogCard(null)
            setRejectDialogAttentionItem(null)
            setRejectError(null)
          }
        }}
        quickRejectReasons={rejectDialogCard?.quick_reject_reasons ?? []}
        defaultQuickRejectReasons={rejectDialogCard?.default_quick_reject_reasons}
        onSubmit={handleRejectSubmit}
        isPending={actionPending && pendingAction === 'reject'}
        error={rejectError}
        agentId={rejectDialogItem?.agent_id}
        dialogTitle={rejectDialogCard?.button_config?.reject_dialog_title}
        dialogDescription={rejectDialogCard?.button_config?.reject_dialog_description}
        submitLabel={rejectDialogCard?.button_config?.reject_submit_label}
        hideQuickReasons={!!rejectDialogCard?.button_config?.suppress_label}
        submitVariant={rejectDialogCard?.button_config?.suppress_label ? 'default' : 'destructive'}
        sourceContext={buildSourceContext(rejectDialogCard?.sections)}
        proposedResponse={buildProposedResponse(rejectDialogCard?.sections)}
      />

      {/* Escalate confirm dialog */}
      <Dialog open={!!escalateDialogItem} onOpenChange={(open) => !open && setEscalateDialogItem(null)}>
        <DialogContent style={{ maxWidth: '448px' }}>
          <DialogHeader>
            <DialogTitle>Escalate to Human Review?</DialogTitle>
            <DialogDescription>
              This will remove the item from your queue and flag it for manual intervention.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter style={{ display: 'flex', gap: '8px' }}>
            <Button variant="secondary" onClick={() => setEscalateDialogItem(null)} disabled={actionPending && pendingAction === 'reject'}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleEscalateSubmit} disabled={actionPending && pendingAction === 'reject'}>
              {(actionPending && pendingAction === 'reject') ? 'Escalating...' : 'Escalate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/*
        Agent session popout is mounted globally in `App.tsx` — see the
        comment there. It reads its target sessionId from
        useQueueStore.agentSessionModalSessionId, so opening it from any
        surface (QueueView, AttentionInspector, StepCard, ...) "just works".
      */}
    </div>
  )
}

/**
 * ApprovalCardPanel component.
 *
 * Renders the full registry-driven approval card for tool approval items.
 *
 * When the attention item is linked to an agent session, the card's
 * header surfaces a "View Agent Session" icon button that opens the
 * agent session modal popout — letting operators inspect the agent's
 * full reasoning + tool-call chain that led up to the approval request.
 */
interface ApprovalCardPanelProps {
  /** The agent session id linked to this approval item, if any. */
  sessionId: string | null
  /**
   * The backing execution id, if any. Threaded through to the
   * approval card so the execution-summary section renderer can
   * call `/v1/executions/{id}/summarise` on demand. `null` when
   * the attention item isn't backed by an execution; the summary
   * renderer falls back to its non-interactive empty state.
   *
   * Sourced from `AttentionItem.execution_id` at the call site
   * rather than `ApprovalDetailResponse.item` because the latter
   * (`ApprovalItem`) doesn't carry it directly.
   */
  executionId: string | null
  detailData: ApprovalDetailResponse | null
  detailLoading: boolean
  detailError: string | null
  onRetryDetail: () => void
  onApprove: () => void
  onSuppress: () => void
  onReject: (card: ApprovalDetailResponse['card']) => void
  onEscalate: () => void
  actionPending: boolean
  pendingAction: 'approve' | 'suppress' | 'reject' | null
  /**
   * When true the action bar is hidden. Used for resolved history items
   * where approve/reject/suppress are no longer applicable.
   */
  readOnly?: boolean
}

function ApprovalCardPanel({
  sessionId,
  executionId,
  detailData,
  detailLoading,
  detailError,
  onRetryDetail,
  onApprove,
  onSuppress,
  onReject,
  onEscalate,
  actionPending,
  pendingAction,
  readOnly = false,
}: ApprovalCardPanelProps) {
  return (
    <div style={styles.panelContainer}>
      {/* Loading state */}
      {detailLoading && <ApprovalCardSkeleton />}

      {/* Error state */}
      {detailError && (
        <ApprovalCardError
          error={detailError}
          onRetry={onRetryDetail}
        />
      )}

      {/* Loaded state.
       *
       * `sessionId` is threaded through so the header can render its
       * "View Agent Session" icon button — the dedicated
       * SessionContextSummary chip is no longer rendered below the
       * card because that surface area was duplicative once the
       * action bar moved to the top of the card.
       *
       * The `ApprovalExecutionProvider` wrapper exposes the backing
       * execution id to descendant renderers so the execution-summary
       * section can call `/v1/executions/{id}/summarise` on demand
       * without us having to widen the generic `RendererProps`
       * contract. `execution_id` is sourced from the AttentionItem
       * directly (it's already on `detailData.item`). When the item
       * has no execution, the provider passes `null` and the summary
       * renderer silently falls back to its non-interactive empty
       * state. */}
      {detailData && (
        <ApprovalExecutionProvider executionId={executionId}>
          <ApprovalCard
            item={detailData.item}
            card={detailData.card}
            onApprove={onApprove}
            onSuppress={onSuppress}
            onReject={() => onReject(detailData.card)}
            onEscalate={onEscalate}
            actionPending={actionPending}
            pendingAction={pendingAction}
            sessionId={sessionId}
            readOnly={readOnly}
          />
        </ApprovalExecutionProvider>
      )}
    </div>
  )
}

export default QueueView

const iconStyles: Record<string, CSSProperties> = {
  icon16mr: {
    width: '16px',
    height: '16px',
    marginRight: '8px',
  },
  icon12: {
    width: '12px',
    height: '12px',
  },
  icon14: {
    width: '14px',
    height: '14px',
  },
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  errorContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    paddingTop: '32px',
    paddingBottom: '32px',
    textAlign: 'center',
  },
  errorIcon: {
    width: '40px',
    height: '40px',
    color: 'var(--error)',
  },
  errorTitle: {
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  errorMessage: {
    marginTop: '4px',
    fontSize: '14px',
    color: 'var(--text-muted)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  typeFilterSelect: {
    width: '150px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  updateIndicator: {
    fontSize: '11px',
    color: 'var(--text-muted)',
  },
  updatingText: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  itemList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    // Keep the rows inside the view pane. Without this the list takes its
    // width from the widest card, which then scrolls horizontally and hides
    // each row's right-hand controls (time, assignment chip, suppress).
    minWidth: 0,
  },
  panelContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
}
